import React, { FunctionComponent } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  IconButton,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';
import { BloodUpload } from '../../../../models/BloodUpload';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

type Props = {
  object: User;
}

export const CoachUserWidgetsBasicMeasureUploads: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  // const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  // Pre-format dates as strings (Luxon, same output as <Dt />) so static +
  // value can live in ONE Typography string child — required for the html
  // attribute to engage (it only applies to single-string children).
  // Upload dates are stored as dd/LL/y (the DatePicker's format), older rows
  // can carry ISO — hence the hyphen split before parsing.
  const fmtDay = (d?: string) => {
    if (!d) return '';
    const dt = d.indexOf('-') > -1 ? DateTime.fromISO(d) : DateTime.fromFormat(d, 'dd/LL/y');
    return dt.setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'});
  }
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  let totalCount = 0;

  // Coach-level widget settings: composite { cta, counts, list } under one
  // value key. Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.basicMeasureUploadsValueKey];
  const showCta = HelpersClientWidgets.getBasicMeasureUploadsCtaEnabled(cfg);
  const showCounts = HelpersClientWidgets.getBasicMeasureUploadsCountsEnabled(cfg);
  const showList = HelpersClientWidgets.getBasicMeasureUploadsListEnabled(cfg);

  // The blood page's visible-set query (filter copied verbatim). Rows with a
  // practice are coach uploads, rows without are client uploads — the split
  // happens client-side below. `d.count` is the total regardless of per_page.
  const {
    data: uploads,
  } = useQuery({
    queryKey: ["blood_uploads", object?.id],
    queryFn: () =>
      fetch_all_with_count<BloodUpload>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'blood_upload',
          fields: ['id', 'created_at', 'get_item', 'date', 'hide_practices', 'item_file_name', 'practice', 'visible_for_client', 'user_id'],
          sub_fields: {
            practice: ['id', 'name']
          },
          order: 'date DESC',
          per_page: 20,
          page: 0,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id),
              for_practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => {
        totalCount = d.count;
        return d.result
      },
    enabled: !!object?.id && !!login?.id,
  });

  const rows = uploads || [];
  const clientUploads = rows.filter((row) => !row.practice?.id);
  const coachUploads = rows.filter((row) => !!row.practice?.id);

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row. Gated by
        the coach's `cta` setting. No premium lock, the file page's tabs gate
        their own modules. */}
    {showCta && <Button
      label={t("coach.general.actions.to_blood")}
      id="goto_blood_uploads"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}/file`, {state: {tab: 5}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.basic_measure_uploads")}</Typography>
        <Divider />
      </Grid>

      {/* Summary counts: one i18n plural pair per source (client / coach
          uploads), separated by a middot. Only meaningful when at least one
          upload is visible. */}
      {showCounts && totalCount > 0 && <Grid item xs={12}>
        <Typography variant="body2" html>
          {`${t("coach.submenu.detail.blood_uploads_client_count", {count: clientUploads.length})} · ${t("coach.submenu.detail.blood_uploads_coach_count", {count: coachUploads.length})}`}
        </Typography>
      </Grid>}

      {/* Upload rows: file name + its date, a Divider between consecutive
          rows. Toggled by the show_list setting (the counts line stays
          regardless). */}
      {showList && rows.map((row, idx) => <React.Fragment key={row.id}>
        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Grid container spacing={0} sx={{flex: 1, minWidth: 0}}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{row.item_file_name || '-'}</Typography>
              <Typography variant="caption" sx={{color: 'text.secondary'}}>{fmtDay(row.date)}</Typography>
            </Grid>
          </Grid>
          {!!row.get_item && <IconButton
            title={"View"}
            sx={{padding: 0.5}}
            onClick={() => {
              window.open(row.get_item, "_BLANK");
            }}
          >
            <VisibilityIcon sx={{fontSize: '1.1rem'}} />
          </IconButton>}
        </Grid>
        {idx < rows.length - 1 && <Grid item xs={12}>
          <Divider />
        </Grid>}
      </React.Fragment>)}
    </Grid>
  </Paper>;
}
